import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { Benutzer } from '../../models/benutzer.model';
import { Rolle } from '../../models/rolle.model';

import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

export type RollenAendernAktion = 'replace' | 'add' | 'remove';

export interface RollenAendernDialogData {
  selectedUsers: Benutzer[];
  rollen: Rolle[];
}

export interface RollenAendernDialogResult {
  action: RollenAendernAktion;
  selectedRoleNames: string[];
}

interface ActionOption {
  value: RollenAendernAktion;
  label: string;
  description: string;
}

@Component({
  selector: 'app-rollen-aendern-dialog',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './rollen-aendern-dialog.component.html',
  styleUrl: './rollen-aendern-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RollenAendernDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<RollenAendernDialogComponent, RollenAendernDialogResult>
  );

  private readonly snackBar = inject(MatSnackBar);

  protected readonly data = inject<RollenAendernDialogData>(MAT_DIALOG_DATA);

  protected readonly isActionDropdownOpen = signal(false);
  protected readonly selectedAction = signal<RollenAendernAktion>('replace');
  protected readonly selectedRoleNames = signal<string[]>([]);

  protected readonly actionOptions: ActionOption[] = [
    {
      value: 'replace',
      label: 'Rollen ersetzen',
      description:
        'Alle bestehenden Rollen der ausgewählten Benutzer werden durch die gewählten ersetzt.',
    },
    {
      value: 'add',
      label: 'Rollen hinzufügen',
      description:
        'Die gewählten Rollen werden den ausgewählten Benutzern zusätzlich hinzugefügt.',
    },
    {
      value: 'remove',
      label: 'Rollen entfernen',
      description:
        'Die gewählten Rollen werden von den ausgewählten Benutzern entfernt.',
    },
  ];

  protected readonly currentAction = computed(() => {
    return this.actionOptions.find((option) => option.value === this.selectedAction())!;
  });

  protected close(): void {
    this.dialogRef.close();
  }

  protected toggleActionDropdown(): void {
    this.isActionDropdownOpen.update((value) => !value);
  }

  protected selectAction(action: RollenAendernAktion): void {
    this.selectedAction.set(action);
    this.isActionDropdownOpen.set(false);
  }

  protected toggleRole(roleName: string): void {
    const current = this.selectedRoleNames();

    if (current.includes(roleName)) {
      this.selectedRoleNames.set(current.filter((name) => name !== roleName));
      return;
    }

    this.selectedRoleNames.set([...current, roleName]);
  }

  protected isRoleSelected(roleName: string): boolean {
    return this.selectedRoleNames().includes(roleName);
  }

  protected submit(): void {
    if (!this.selectedRoleNames().length) {
      this.snackBar.open(
        'Bitte wähle mindestens eine Rolle.',
        '✕',
        {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar'],
        }
      );

      return;
    }

    this.dialogRef.close({
      action: this.selectedAction(),
      selectedRoleNames: this.selectedRoleNames(),
    });
  }
}